import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { templatesApi } from '@/api/templates';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import type { BulkRunView, TemplateVersionView } from '@/types/api';

/** Versions a WordPress site can be created from or moved to: published, not deprecated. */
export function useActiveTemplates({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.templates.active,
    queryFn: () => templatesApi.list(),
    enabled,
    select: (page) => page.items.filter((version) => !version.deprecated && version.engine === 'wordpress'),
  });
}

/** The templates screen: every version, deprecated ones too. */
export function useTemplateVersions() {
  return useInfiniteQuery({
    queryKey: queryKeys.templates.list,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => templatesApi.list(pageParam),
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useDeprecateTemplate(callbacks?: MutationCallbacks<TemplateVersionView, string>) {
  return useApiMutation(templatesApi.deprecate, () => [queryKeys.templates.all], callbacks);
}

/** Templates by name, with versions, sites and the reference site (ADR-0047). */
export function useTemplateFamilies({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: queryKeys.templates.families, queryFn: () => templatesApi.families(), enabled });
}

export function useRollout(callbacks?: MutationCallbacks<BulkRunView, { templateId: string; versionId: string }>) {
  return useApiMutation(
    ({ templateId, versionId }) => templatesApi.rollout(templateId, versionId),
    () => [queryKeys.templates.all, queryKeys.bulk.all, queryKeys.sites.all],
    callbacks,
  );
}
